import React, { useEffect, useState, useMemo, useRef, useCallback } from 'react';
import { DataTablePaginated } from '../components';
import { axios } from '../libs/axios';
import ReactPaginate from 'react-paginate';
import logger from '../utils/logger';

const DENSITY_MODES = {
  compact: 'compact',
  standard: 'standard',
  detailed: 'detailed',
};

const STORAGE_KEYS = {
  density: 'hugo.subscriptions.density',
  planFilter: 'hugo.subscriptions.planFilter',
  statusFilter: 'hugo.subscriptions.statusFilter',
};

const STATUS_FILTER_OPTIONS = ['initiated', 'active', 'past_due', 'paused', 'stopped'];

function readStoredDensity() {
  try {
    const value = localStorage.getItem(STORAGE_KEYS.density);
    return Object.values(DENSITY_MODES).includes(value) ? value : DENSITY_MODES.standard;
  } catch {
    return DENSITY_MODES.standard;
  }
}

function readStoredPlanFilter() {
  try {
    return localStorage.getItem(STORAGE_KEYS.planFilter) || '';
  } catch {
    return '';
  }
}

function readStoredStatusFilter() {
  try {
    return localStorage.getItem(STORAGE_KEYS.statusFilter) || '';
  } catch {
    return '';
  }
}

const STATUS_BADGE_CLASS = {
  active: 'badge-status-active',
  initiated: 'badge-status-initiated',
  past_due: 'badge-status-past-due',
  paused: 'bg-secondary',
  stopped: 'bg-secondary',
};

function formatDateDMY(cell) {
  if (!cell) return '';
  const date = new Date(cell);
  const day = date.getDate().toString().padStart(2, '0');
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const year = date.getFullYear();
  const hours = date.getHours().toString().padStart(2, '0');
  const minutes = date.getMinutes().toString().padStart(2, '0');
  return `${day}.${month}.${year} ${hours}:${minutes}`;
}

function formatNextBillingCell(_, sub) {
  if (!sub) return '';
  if (sub.cancelAtPeriodEnd) {
    const until = sub.accessUntil || sub.nextBillingDate;
    if (!until) return 'Active until period end (no renewal)';
    return `Active until ${formatDateDMY(until)} (no renewal)`;
  }
  return formatDateDMY(sub.nextBillingDate);
}

function isBillingOverdue(sub) {
  if (!sub?.nextBillingDate) return false;
  if (['paused', 'stopped'].includes(sub.status)) return false;
  if (sub.cancelAtPeriodEnd) return false;
  return new Date(sub.nextBillingDate) <= new Date();
}

function isRechargeDue(sub) {
  if (!sub) return false;
  if (!['active', 'past_due'].includes(sub.status)) return false;
  if (sub.cancelAtPeriodEnd) return false;
  if (!sub.nextBillingDate) return false;
  return new Date(sub.nextBillingDate) <= new Date();
}

function canRecharge(sub) {
  return isRechargeDue(sub) && !!sub.token;
}

function confirmAction({ title, text, onYes }) {
  let ntfy = window.nConfirm({
    icon: 'fa-question-circle',
    title,
    text: text || '',
    onYes: async () => {
      try {
        await onYes();
      } finally {
        ntfy?.close?.();
      }
    },
    onNo: () => ntfy?.close?.(),
  });
}

const RECHARGE_DISABLED_TITLE = 'Only available for active or past_due subscriptions with a saved card and next billing date in the past';
const REFUND_DISABLED_TITLE = 'Only available when the last payment is settled and a paid invoice exists';
const NO_TOKEN_TITLE = 'Subscription has no saved payment token';

function showRechargeMenuItem(sub) {
  return !!sub && ['active', 'past_due'].includes(sub.status);
}

function getRechargeDisabledReason(sub) {
  if (!sub) return RECHARGE_DISABLED_TITLE;
  if (!['active', 'past_due'].includes(sub.status)) {
    return `Status is ${sub.status || 'unknown'} — recharge only for active or past_due`;
  }
  if (sub.cancelAtPeriodEnd) {
    return 'Scheduled to cancel at period end — use Resume auto-renewal first';
  }
  if (!sub.nextBillingDate) {
    return 'Next billing date is not set';
  }
  if (new Date(sub.nextBillingDate) > new Date()) {
    return `Next billing is ${formatDateDMY(sub.nextBillingDate)} — not yet due`;
  }
  if (!sub.token) return NO_TOKEN_TITLE;
  return null;
}

function getAvailableActions(sub) {
  if (!sub) {
    return {
      showRecharge: false,
      canRecharge: false,
      rechargeDisabledReason: RECHARGE_DISABLED_TITLE,
      showResumeRenewal: false,
      showPause: false,
      showResume: false,
      showCancelPeriodEnd: false,
      showCancelImmediate: false,
      showRefund: false,
      canRefund: false,
      showDelete: false,
    };
  }

  const { status } = sub;
  const showRecharge = showRechargeMenuItem(sub);
  const showPause = ['active', 'past_due'].includes(status);
  const showResume = status === 'paused';
  const showResumeRenewal = ['active', 'past_due'].includes(status) && !!sub.cancelAtPeriodEnd;
  const showCancelPeriodEnd = ['active', 'past_due', 'paused'].includes(status) && !sub.cancelAtPeriodEnd;
  const showCancelImmediate = status !== 'stopped';
  const showRefund = !(status === 'stopped' && sub.stopReason === 'refunded');
  const canDoRefund = canRefund(sub);

  return {
    showRecharge,
    canRecharge: canRecharge(sub),
    rechargeDisabledReason: getRechargeDisabledReason(sub),
    showResumeRenewal,
    showPause,
    showResume,
    showCancelPeriodEnd,
    showCancelImmediate,
    showRefund,
    canRefund: canDoRefund,
    showDelete: status !== 'active',
  };
}

function toDatetimeLocal(value) {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function canRefund(sub) {
  if (!sub) return false;
  if (sub.status === 'stopped' && sub.stopReason === 'refunded') return false;
  return sub.lastPaymentStatus === 'settled' && !!sub.invoiceNo;
}

function getActionError(data) {
  if (!data || typeof data.status !== 'number' || data.status < 400) return null;
  return data.message || 'Action failed';
}

function getRechargeOutcome(data) {
  if (data?.skipped) {
    return { type: 'skipped', message: data.message || data.code || 'Charge skipped' };
  }
  if (data?.failed) {
    return { type: 'failed', message: data.message || `Payment failed (${data.stage || 'unknown'})` };
  }
  const r = data?.result;
  if (r?.skipped) return { type: 'skipped', message: r.reason || 'Charge skipped' };
  if (r?.failed) return { type: 'failed', message: `Payment failed (${r.stage})` };
  if (data?.outcome === 'settled' || r?.payment_state === 'settled') return { type: 'settled' };
  if (data?.outcome === 'pending' || r?.payment_state) {
    return { type: 'pending', message: r?.payment_state };
  }
  return { type: 'unknown' };
}

function StatusBadge({ status, stopReason }) {
  const cls = STATUS_BADGE_CLASS[status] || 'bg-light text-dark';
  const label = stopReason && status === 'stopped' ? `${status} (${stopReason})` : status;
  return <span className={`badge ${cls}`}>{label || '—'}</span>;
}

function SubscriptionActionsMenu({ row, runAction, onRecharge, onDelete, subscriptionTestMode }) {
  const [isOpen, setIsOpen] = useState(false);
  const rootRef = useRef(null);

  useEffect(() => {
    if (!isOpen) return undefined;
    let attached = false;
    const onDocMouseDown = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    const timer = window.setTimeout(() => {
      document.addEventListener('mousedown', onDocMouseDown);
      attached = true;
    }, 0);
    return () => {
      window.clearTimeout(timer);
      if (attached) document.removeEventListener('mousedown', onDocMouseDown);
    };
  }, [isOpen]);

  const close = () => setIsOpen(false);
  const run = (label, confirmTitle, confirmText, fn) => {
    close();
    confirmAction({
      title: confirmTitle,
      text: confirmText || `<span>Client: ${row.firstname} ${row.lastname}</span>`,
      onYes: () => runAction(label, fn),
    });
  };
  const actions = getAvailableActions(row);

  const rechargeDisabledTitle = actions.rechargeDisabledReason || RECHARGE_DISABLED_TITLE;

  return (
    <div
      ref={rootRef}
      className="action action-more"
      data-sub-actions
      onClick={(e) => e.stopPropagation()}
    >
      <button
        type="button"
        className={`button toggle${isOpen ? ' active' : ''}`}
        onMouseDown={(e) => e.stopPropagation()}
        onClick={(e) => {
          e.stopPropagation();
          setIsOpen((open) => !open);
        }}
      >
        ⋮
      </button>
      {isOpen && (
        <div className="dropdown-more subscription-actions-dropdown">
          {actions.showRecharge && (
            <button
              type="button"
              className="item"
              disabled={!actions.canRecharge}
              title={!actions.canRecharge ? rechargeDisabledTitle : undefined}
              onClick={(e) => {
                e.stopPropagation();
                if (!actions.canRecharge) return;
                close();
                confirmAction({
                  title: 'Recharge subscription now?',
                  text: `<span>Client: ${row.firstname} ${row.lastname}</span>`,
                  onYes: () => onRecharge(row),
                });
              }}
            >
              Recharge now
            </button>
          )}
          {actions.showResumeRenewal && (
            <button
              type="button"
              className="item"
              onClick={(e) => {
                e.stopPropagation();
                run(
                  'Auto-renewal resumed',
                  'Resume auto-renewal?',
                  'Clears cancel-at-period-end so billing and recharge can continue.',
                  () => axios.post(`/subscriptions/${row._id}/resume-renewal`),
                );
              }}
            >
              Resume auto-renewal
            </button>
          )}
          {actions.showResume && (
            <button
              type="button"
              className="item"
              onClick={(e) => {
                e.stopPropagation();
                run('Resumed', 'Resume subscription?', null, () => axios.post(`/subscriptions/${row._id}/resume`));
              }}
            >
              Resume
            </button>
          )}
          {actions.showPause && (
            <button
              type="button"
              className="item"
              onClick={(e) => {
                e.stopPropagation();
                run('Paused', 'Pause subscription?', null, () => axios.post(`/subscriptions/${row._id}/pause`));
              }}
            >
              Pause
            </button>
          )}
          {actions.showCancelPeriodEnd && (
            <button
              type="button"
              className="item"
              onClick={(e) => {
                e.stopPropagation();
                run('Cancel at period end', 'Cancel at period end?', null, () => axios.post(`/subscriptions/${row._id}/cancel`, { mode: 'period_end' }));
              }}
            >
              Cancel at period end
            </button>
          )}
          {actions.showCancelImmediate && (
            <button
              type="button"
              className="item"
              onClick={(e) => {
                e.stopPropagation();
                run('Canceled immediately', 'Cancel subscription immediately?', null, () => axios.post(`/subscriptions/${row._id}/cancel`, { mode: 'immediate' }));
              }}
            >
              Cancel immediately
            </button>
          )}
          {actions.showRefund && (
            <button
              type="button"
              className="item"
              disabled={!actions.canRefund}
              title={!actions.canRefund ? REFUND_DISABLED_TITLE : undefined}
              onClick={(e) => {
                e.stopPropagation();
                run('Refunded', 'Refund last payment?', null, () => axios.post(`/subscriptions/${row._id}/refund`));
              }}
            >
              Refund
            </button>
          )}
          {subscriptionTestMode && (
            <>
              <button
                type="button"
                className="item"
                onClick={(e) => {
                  e.stopPropagation();
                  run(
                    'Test decline off',
                    'Clear test decline?',
                    'Next recharge will use the real EveryPay charge path.',
                    () => axios.post(`/subscriptions/${row._id}/test-decline`, { testDecline: null }),
                  );
                }}
              >
                Test decline: off{!row.testDecline ? ' ✓' : ''}
              </button>
              <button
                type="button"
                className="item"
                onClick={(e) => {
                  e.stopPropagation();
                  run(
                    'Test decline soft',
                    'Set test decline to soft?',
                    'Next recharge will simulate a soft decline (no EveryPay call).',
                    () => axios.post(`/subscriptions/${row._id}/test-decline`, { testDecline: 'soft' }),
                  );
                }}
              >
                Test decline: soft{row.testDecline === 'soft' ? ' ✓' : ''}
              </button>
              <button
                type="button"
                className="item"
                onClick={(e) => {
                  e.stopPropagation();
                  run(
                    'Test decline hard',
                    'Set test decline to hard?',
                    'Next recharge will simulate a hard decline (no EveryPay call).',
                    () => axios.post(`/subscriptions/${row._id}/test-decline`, { testDecline: 'hard' }),
                  );
                }}
              >
                Test decline: hard{row.testDecline === 'hard' ? ' ✓' : ''}
              </button>
            </>
          )}
          {actions.showDelete && (
            <button
              type="button"
              className="item"
              onClick={(e) => {
                e.stopPropagation();
                close();
                onDelete(row);
              }}
            >
              Delete
            </button>
          )}
        </div>
      )}
    </div>
  );
}

function formatActivityLine(event) {
  const when = formatDateDMY(event.createdAt);
  const statusPart = event.from_status && event.to_status && event.from_status !== event.to_status
    ? `${event.from_status} -> ${event.to_status} — `
    : '';
  const declinePart = event.type === 'charge_failed' && event.data?.declineClass
    ? ` (${event.data.declineClass})`
    : '';
  return `${when} — ${event.actor} — ${statusPart}${event.message}${declinePart}`;
}

function formatNextRetryCell(_, sub) {
  if (!sub || sub.status !== 'past_due' || !sub.nextRetryAt) return '—';
  return formatDateDMY(sub.nextRetryAt);
}

function formatTestDeclineLabel(value) {
  if (value === 'soft' || value === 'hard') return value;
  return 'off';
}

export const Subscriptions = () => {
  const [subscriptions, setSubscriptions] = useState([]);
  const [rawSubs, setRawSubs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [sizePerPage, setSizePerPage] = useState(100);
  const [search, setSearch] = useState('');
  const [density, setDensity] = useState(readStoredDensity);
  const [planFilter, setPlanFilter] = useState(readStoredPlanFilter);
  const [statusFilter, setStatusFilter] = useState(readStoredStatusFilter);
  const [planOptions, setPlanOptions] = useState([]);
  const [overdueOnly, setOverdueOnly] = useState(false);

  const [showModal, setShowModal] = useState(false);
  const [selectedSubscription, setSelectedSubscription] = useState(null);
  const [relatedInvoices, setRelatedInvoices] = useState([]);
  const [activityEvents, setActivityEvents] = useState([]);
  const [editedServiceStart, setEditedServiceStart] = useState('');
  const [editedNextBilling, setEditedNextBilling] = useState('');
  const [editedServiceEnd, setEditedServiceEnd] = useState('');
  const [sortField, setSortField] = useState('createdAt');
  const [sortOrder, setSortOrder] = useState('desc');
  const [subscriptionTestMode, setSubscriptionTestMode] = useState(false);

  const fetchSubscriptions = async (page = 1, size = 100, q = '', sort = 'createdAt', order = 'desc', plan = '', status = '') => {
    setLoading(true);
    try {
      const params = { page, sizePerPage: size, q, sortField: sort, sortOrder: order };
      if (plan) params.plan = plan;
      if (status) params.status = status;
      const { subscriptions: rows, total } = await axios.get('/subscriptions', { params });
      setRawSubs(rows);
      setTotalRecords(total);
      setSubscriptions(rows);
    } finally {
      setLoading(false);
    }
  };

  const fetchRelatedInvoices = async (subscriptionId) => {
    try {
      const { invoices } = await axios.get('/invoices', {
        params: { qtype: 'by_subscription', subscription_id: subscriptionId }
      });
      setRelatedInvoices(invoices);
    } catch (err) {
      logger.error('Error fetching invoices:', err);
      setRelatedInvoices([]);
    }
  };

  const fetchActivityEvents = async (subscriptionId) => {
    try {
      const { events } = await axios.get(`/subscriptions/${subscriptionId}/events`, {
        params: { limit: 50 },
      });
      setActivityEvents(events || []);
    } catch (err) {
      logger.error('Error fetching subscription events:', err);
      setActivityEvents([]);
    }
  };

  const openView = async (sub) => {
    setSelectedSubscription(sub);
    setEditedServiceStart(toDatetimeLocal(sub.serviceStartDate));
    setEditedNextBilling(toDatetimeLocal(sub.nextBillingDate));
    setEditedServiceEnd(toDatetimeLocal(sub.serviceEndDate));
    await Promise.all([
      fetchRelatedInvoices(sub._id),
      fetchActivityEvents(sub._id),
    ]);
    setShowModal(true);
  };

  useEffect(() => {
    fetchSubscriptions(currentPage, sizePerPage, search, sortField, sortOrder, planFilter, statusFilter);
  }, [currentPage, sizePerPage, search, sortField, sortOrder, planFilter, statusFilter]);

  useEffect(() => {
    axios.get('/crm/settings')
      .then((data) => {
        const plans = data?.subscriptionPlans || {};
        const keys = Object.keys(plans).sort();
        setPlanOptions(keys.map((key) => ({
          key,
          label: plans[key]?.name ? `${key} (${plans[key].name})` : key,
        })));
      })
      .catch((err) => logger.error('Error fetching subscription plans:', err));
  }, []);

  useEffect(() => {
    axios.get('/subscriptions/billing-config')
      .then((data) => {
        setSubscriptionTestMode(!!data?.subscriptionTestMode);
      })
      .catch((err) => logger.error('Error fetching subscription billing config:', err));
  }, []);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEYS.density, density);
    } catch {
      // ignore storage errors
    }
  }, [density]);

  useEffect(() => {
    try {
      if (planFilter) {
        localStorage.setItem(STORAGE_KEYS.planFilter, planFilter);
      } else {
        localStorage.removeItem(STORAGE_KEYS.planFilter);
      }
    } catch {
      // ignore storage errors
    }
  }, [planFilter]);

  useEffect(() => {
    try {
      if (statusFilter) {
        localStorage.setItem(STORAGE_KEYS.statusFilter, statusFilter);
      } else {
        localStorage.removeItem(STORAGE_KEYS.statusFilter);
      }
    } catch {
      // ignore storage errors
    }
  }, [statusFilter]);

  const handleTableChange = (type, { sortField: nextSortField, sortOrder: nextSortOrder }) => {
    if (type === 'sort' && nextSortField) {
      setSortField(nextSortField);
      setSortOrder(nextSortOrder);
      setCurrentPage(1);
    }
  };

  useEffect(() => {
    const rows = overdueOnly ? rawSubs.filter(isBillingOverdue) : rawSubs;
    setSubscriptions(rows);
  }, [rawSubs, overdueOnly]);

  const runAction = useCallback(async (label, fn) => {
    try {
      const data = await fn();
      const actionErr = getActionError(data);
      if (actionErr) {
        window.nError?.({
          title: 'Error',
          text: actionErr,
          icon: 'fa-exclamation-triangle',
        });
        return;
      }
      fetchSubscriptions(currentPage, sizePerPage, search, sortField, sortOrder, planFilter, statusFilter);
      window.nSuccess?.({ title: 'Done', text: label, icon: 'fa-check-square' });
    } catch (err) {
      logger.error(err);
      window.nError?.({
        title: 'Error',
        text: err.response?.data?.message || err.message || 'Action failed',
        icon: 'fa-exclamation-triangle',
      });
    }
  }, [currentPage, sizePerPage, search, sortField, sortOrder, planFilter, statusFilter]);

  const handleRecharge = useCallback(async (row) => {
    try {
      const data = await axios.post(`/subscriptions/${row._id}/recharge`);
      const actionErr = getActionError(data);
      if (actionErr) {
        window.nError?.({
          title: 'Recharge failed',
          text: actionErr,
          icon: 'fa-exclamation-triangle',
        });
        return;
      }
      const outcome = getRechargeOutcome(data);
      fetchSubscriptions(currentPage, sizePerPage, search, sortField, sortOrder, planFilter, statusFilter);
      if (outcome.type === 'skipped' || outcome.type === 'failed') {
        window.nError?.({
          title: 'Recharge failed',
          text: outcome.message || 'Charge could not be completed',
          icon: 'fa-exclamation-triangle',
        });
        return;
      }
      if (outcome.type === 'settled') {
        window.nSuccess?.({
          title: 'Recharged',
          text: 'Payment settled successfully',
          icon: 'fa-check-square',
        });
        return;
      }
      if (outcome.type === 'pending') {
        window.nSuccess?.({
          title: 'Charge initiated',
          text: 'Payment processing — row updates when payment settles',
          icon: 'fa-check-square',
        });
        return;
      }
      window.nSuccess?.({
        title: 'Charge initiated',
        text: 'Recharge request submitted',
        icon: 'fa-check-square',
      });
    } catch (err) {
      logger.error(err);
      window.nError?.({
        title: 'Recharge failed',
        text: err.response?.data?.message || err.message || 'Action failed',
        icon: 'fa-exclamation-triangle',
      });
    }
  }, [currentPage, sizePerPage, search, sortField, sortOrder, planFilter, statusFilter]);

  const handleDelete = useCallback((subs) => {
    confirmAction({
      title: 'Delete subscription?',
      text: `<span>Client: ${subs.firstname} ${subs.lastname}</span>`,
      onYes: async () => {
        try {
          await axios.delete(`/subscriptions/${subs._id}`);
          fetchSubscriptions(currentPage, sizePerPage, search, sortField, sortOrder, planFilter, statusFilter);
        } catch (err) {
          logger.error('Delete error:', err);
        }
      },
    });
  }, [currentPage, sizePerPage, search, sortField, sortOrder, planFilter, statusFilter]);

  const columnDefs = useMemo(() => ({
    client: {
      dataField: 'lastname',
      text: 'Client',
      classes: 'cell-name',
      headerClasses: 'cell-name',
      formatter: (_, r) => (
        <div>
          <div>{r.firstname} {r.lastname}</div>
          <div style={{ fontSize: '12px', color: '#666' }}>{r.email}</div>
        </div>
      ),
    },
    plan: { dataField: 'plan', text: 'Plan', classes: 'cell-text', headerClasses: 'cell-text' },
    status: {
      dataField: 'status',
      text: 'Status',
      formatter: (_, r) => <StatusBadge status={r.status} stopReason={r.stopReason} />,
    },
    lastCharge: {
      dataField: 'lastPaymentDate',
      text: 'Last charge',
      classes: 'cell-date',
      headerClasses: 'cell-date',
      formatter: formatDateDMY,
    },
    nextBilling: {
      dataField: 'nextBillingDate',
      text: 'Next Billing',
      classes: 'cell-date',
      headerClasses: 'cell-date',
      formatter: formatNextBillingCell,
    },
    amount: {
      dataField: 'plan',
      text: 'Amount',
      sort: false,
      formatter: (_, r) => r.amount_total != null ? `${r.amount_total}€` : '—',
    },
    invoice: {
      dataField: 'invoiceNo',
      text: 'Invoice',
      formatter: (_, r) => (
        <div>
          <div>{r.invoiceNo || '—'}</div>
          {r.lastPaymentStatus && <div style={{ fontSize: '12px', color: '#666' }}>{r.lastPaymentStatus}</div>}
        </div>
      ),
    },
    start: { dataField: 'serviceStartDate', text: 'Start', formatter: formatDateDMY },
    nextRetry: {
      dataField: 'nextRetryAt',
      text: 'Next retry',
      classes: 'cell-date',
      headerClasses: 'cell-date',
      formatter: formatNextRetryCell,
    },
    failed: { dataField: 'failedAttempts', text: 'Failed attempts' },
    actions: {
      dataField: 'actions',
      text: '',
      sort: false,
      classes: 'cell-actions',
      headerClasses: 'cell-actions',
      formatter: (_, r) => (
        <SubscriptionActionsMenu
          row={r}
          runAction={runAction}
          onRecharge={handleRecharge}
          onDelete={handleDelete}
          subscriptionTestMode={subscriptionTestMode}
        />
      ),
    },
  }), [runAction, handleRecharge, handleDelete, subscriptionTestMode]);

  const visibleColumnKeys = useMemo(() => {
    if (density === DENSITY_MODES.compact) {
      return ['client', 'plan', 'status', 'nextBilling', 'invoice', 'actions'];
    }
    if (density === DENSITY_MODES.detailed) {
      return ['client', 'plan', 'status', 'start', 'lastCharge', 'nextBilling', 'nextRetry', 'invoice', 'failed', 'actions'];
    }
    return ['client', 'plan', 'status', 'lastCharge', 'nextBilling', 'invoice', 'actions'];
  }, [density]);

  const tableColumns = useMemo(
    () => visibleColumnKeys.map((k) => columnDefs[k]),
    [visibleColumnKeys, columnDefs],
  );

  return (
    <div id="subscriptions" className="row table_v2 subscriptions">
      <div className="col p-0">
        <div className="x_panel chatbot-builder">
          <div className="header-menu d-flex justify-content-between align-items-center">
            <div className="header-title"><h1>Subscriptions</h1></div>
            <div className="header-actions d-flex align-items-center gap-2">
              <select value={density} onChange={(e) => setDensity(e.target.value)} style={{ marginRight: '8px' }}>
                <option value={DENSITY_MODES.compact}>Compact</option>
                <option value={DENSITY_MODES.standard}>Standard</option>
                <option value={DENSITY_MODES.detailed}>Detailed</option>
              </select>
              <select
                value={planFilter}
                onChange={(e) => { setPlanFilter(e.target.value); setCurrentPage(1); }}
                style={{ marginRight: '8px' }}
              >
                <option value="">All plans</option>
                {planOptions.map((plan) => (
                  <option key={plan.key} value={plan.key}>{plan.label}</option>
                ))}
              </select>
              <select
                value={statusFilter}
                onChange={(e) => { setStatusFilter(e.target.value); setCurrentPage(1); }}
                style={{ marginRight: '8px' }}
              >
                <option value="">All statuses</option>
                {STATUS_FILTER_OPTIONS.map((status) => (
                  <option key={status} value={status}>{status}</option>
                ))}
              </select>
              <button
                type="button"
                className={`button button-small ${overdueOnly ? 'button-blue' : ''}`}
                onClick={() => setOverdueOnly((v) => !v)}
              >
                Billing overdue
              </button>
              <div className="action action-search">
                <input
                  type="text"
                  placeholder="Enter keyword…"
                  value={search}
                  onChange={e => { setSearch(e.target.value); setCurrentPage(1); }}
                />
                <i className="fa-solid fa-magnifying-glass"></i>
              </div>
            </div>
          </div>

          <div className="x_content">
            <div className="page-container d-flex flex-column">
              {loading ? <div>Loading…</div> : (
                <>
                  <div className="page-content nopaginate">
                    <DataTablePaginated
                      data={subscriptions}
                      columns={tableColumns}
                      page={currentPage}
                      sizePerPage={sizePerPage}
                      onPageChange={page => setCurrentPage(page)}
                      onTableChange={handleTableChange}
                      defaultSorted={[{ dataField: 'createdAt', order: 'desc' }]}
                      rowClasses={(row) => (isBillingOverdue(row) ? 'invoice-unpaid' : '')}
                      onRowClick={(row, e) => {
                        if (e?.target?.closest('.cell-actions, [data-sub-actions]')) return;
                        openView(row);
                      }}
                    />
                  </div>

                  <div className="page-footer d-flex justify-content-between align-items-center">
                    <div className="filter filter-pagination d-flex align-items-center">
                      {Math.ceil(totalRecords / sizePerPage) > 0 && (
                        <ReactPaginate
                          containerClassName="pagination"
                          activeClassName="active"
                          previousLinkClassName="fa-regular fa-chevron-left"
                          nextLinkClassName="fa-regular fa-chevron-right"
                          previousClassName={currentPage === 1 ? 'd-none' : 'previous'}
                          nextClassName={currentPage === Math.ceil(totalRecords / sizePerPage) ? 'd-none' : 'next'}
                          previousLabel="Previous"
                          nextLabel="Next"
                          breakLabel="…"
                          pageCount={Math.ceil(totalRecords / sizePerPage)}
                          marginPagesDisplayed={2}
                          pageRangeDisplayed={5}
                          onPageChange={e => setCurrentPage(e.selected + 1)}
                          forcePage={currentPage - 1}
                        />
                      )}
                    </div>
                    <div className="filter filter-pagesize d-flex align-items-center">
                      <label htmlFor="pageSize">Items per page:</label>
                      <select id="pageSize" value={sizePerPage} onChange={e => { setSizePerPage(+e.target.value); setCurrentPage(1); }}>
                        {[100, 200, 300, 500].map(n => <option key={n} value={n}>{n}</option>)}
                      </select>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {showModal && selectedSubscription && (
        <div className="modal" onClick={() => setShowModal(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <span className="modalclose" onClick={() => setShowModal(false)}>&times;</span>
              <h2>Subscription Details</h2>
            </div>
            <div className="modal-form">
              <div className="form-summary">
                <p><strong>Name:</strong> {selectedSubscription.firstname} {selectedSubscription.lastname}</p>
                <p><strong>Email:</strong> {selectedSubscription.email}</p>
                <p><strong>Plan:</strong> {selectedSubscription.plan}</p>
                <p><strong>Status:</strong> <StatusBadge status={selectedSubscription.status} stopReason={selectedSubscription.stopReason} /></p>
                <p>
                  <strong>Start:</strong>{' '}
                  <input
                    type="datetime-local"
                    value={editedServiceStart}
                    onChange={(e) => setEditedServiceStart(e.target.value)}
                  />
                </p>
                <p>
                  <strong>Next Billing:</strong>{' '}
                  <input
                    type="datetime-local"
                    value={editedNextBilling}
                    onChange={(e) => setEditedNextBilling(e.target.value)}
                  />
                </p>
                <p>
                  <strong>End:</strong>{' '}
                  <input
                    type="datetime-local"
                    value={editedServiceEnd}
                    onChange={(e) => setEditedServiceEnd(e.target.value)}
                  />
                </p>
                <p>
                  <button
                    type="button"
                    className="button button-small"
                    onClick={async () => {
                      try {
                        await axios.post(`/subscriptions/${selectedSubscription._id}/dates`, {
                          serviceStartDate: editedServiceStart || null,
                          nextBillingDate: editedNextBilling || null,
                          serviceEndDate: editedServiceEnd || null,
                        });
                            await fetchSubscriptions(currentPage, sizePerPage, search, sortField, sortOrder, planFilter, statusFilter);
                        setShowModal(false);
                        window.nSuccess?.({ title: 'Saved', text: 'Subscription dates updated' });
                      } catch (err) {
                        window.nError?.({
                          title: 'Error',
                          text: err?.response?.data?.message || err?.message || 'Could not update subscription dates',
                          icon: 'fa-exclamation-triangle',
                        });
                      }
                    }}
                  >
                    Save dates
                  </button>
                </p>
                <p><strong>Last charge:</strong> {formatDateDMY(selectedSubscription.lastPaymentDate)}</p>
                <p><strong>Card:</strong> {selectedSubscription.card_info?.holder} ••••{selectedSubscription.card_info?.last4} (Exp: {selectedSubscription.card_info?.expiry})</p>
                <p><strong>Failed Attempts:</strong> {selectedSubscription.failedAttempts}</p>
                <p><strong>Next retry:</strong> {selectedSubscription.nextRetryAt ? formatDateDMY(selectedSubscription.nextRetryAt) : '—'}</p>
                <p><strong>Decline class:</strong> {selectedSubscription.declineClass || '—'}</p>
                {subscriptionTestMode && (
                  <p><strong>Test decline:</strong> {formatTestDeclineLabel(selectedSubscription.testDecline)}</p>
                )}
                <p><strong>Event:</strong> {selectedSubscription.event_name}</p>
                <p><strong>Error:</strong> {selectedSubscription.errormessage}</p>
              </div>

              <div className="related-invoices">
                <h3>Activity</h3>
                {activityEvents.length ? (
                  <ul>
                    {activityEvents.map((event) => (
                      <li key={event._id}>
                        {formatActivityLine(event)}
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p>No activity recorded</p>
                )}
              </div>

              <div className="related-invoices">
                <h3>Related Invoices</h3>
                {relatedInvoices.length ? (
                  <ul>
                    {relatedInvoices.map(inv => (
                      <li key={inv._id}>
                        {inv.invoiceNumber} — {formatDateDMY(inv.createdAt)} — {inv.status} — {inv.amount_total}€
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p>No invoices found</p>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
